import React, { useState, useEffect } from 'react';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';

import { Calendar as CalendarIcon } from "lucide-react";

import Moment from 'moment';
import { motion } from "framer-motion";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Lottie from 'lottie-react';
import * as XLSX from 'xlsx';

import ServiceAuth from 'services/serviceAuth';
import ServiceORG from 'services/serviceOrg';

import TopBar from 'core/widgets/ui/TopBar';

import animBtnLoading from 'assets/anim/anim-btnLoading.json';

export default function LayoutStaff() {

    const nonePermission = ["SUBSCRIPTION.NONE", "TRANSACTION.NONE", "RESTAURANTS.NONE", "STAFF.NONE"];
    const resPermission = ["RESTAURANTS.ALL", "RESTAURANTS.VIEWONLY"];

    const [records, setRecords] = useState([]);

    const [permissions, setPermissions] = useState([]);
    const [modules, setModules] = useState([]);
    const [orgs, setOrgs] = useState([]);

    const [tempDoc, setTempDoc] = useState({});
    const [filter, setFilter] = useState({});

    const [date, setDate] = useState({
        from: Moment().startOf('month').toDate(),
        to: Moment().toDate(),
    });

    const [tab, setTab] = useState('all');
    const [query, setQuery] = useState('');
    const [eid, setEid] = useState('');

    const [pageSize, setPageSize] = useState(10);
    const [pageCurrent, setPageCurrent] = useState(0);
    const [pageTotal, setPageTotal] = useState(0);

    const [isDate, setIsDate] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [submit, setSubmit] = useState(false);

    const authService = new ServiceAuth();
    const orgService = new ServiceORG();

    const userTypes = [
        { value: 'ADMIN', label: 'Admin' },
        { value: 'MANAGER', label: 'Manger' },
        { value: 'PARTNER', label: 'Partner' },
    ];

    const formVSchema = Yup.object().shape({
        role: Yup.string().required('This information is required'),
        fname: Yup.string().required('This information is required'),
        mname: Yup.string().required('This information is required'),
        lname: Yup.string().required('This information is required'),
        email: Yup.string().required('This information is required').email('Please enter valid email address'),
        phone: Yup.string().required('This information is required').matches(/^\d{10}$/, 'Phone number must be 10 digits'),
        street_1: Yup.string().required('This information is required'),
        street_2: Yup.string().required('This information is required'),
        city: Yup.string().required('This information is required'),
        state: Yup.string().required('This information is required'),
        country: Yup.string().required('This information is required'),
        postal_code: Yup.string().required('This information is required').matches(/^\d{6}$/, 'Phone number must be 10 digits'),
    });

    const { values, errors, touched, handleChange, handleSubmit, handleReset, resetForm, setErrors, setValues } = useFormik({
        initialValues: {
            role: '',
            orgs: '',
            fname: '',
            mname: '',
            lname: '',
            email: '',
            phone: '',
            street_1: '',
            street_2: '',
            city: '',
            state: '',
            country: '',
            postal_code: '',
        },
        validationSchema: formVSchema,
        enableReinitialize: true,
        onSubmit: values => {
            if (isOpen && !isEdit) {
                setSubmit(true);
                var body = {
                    fname: values.fname,
                    mname: values.mname,
                    lname: values.lname,
                    email: values.email,
                    phone: values.phone,
                    dob: Moment().toISOString(),
                    addresses: [
                        {
                            title: "HOME",
                            default: true,
                            street_1: values.street_1,
                            street_2: values.street_2,
                            city: values.city.toUpperCase(),
                            state: values.state.toUpperCase(),
                            country: values.country.toUpperCase(),
                            postal_code: values.postal_code,
                        }
                    ],
                    password: values.phone,
                    media: {},
                    role: values.role,
                    orgs: [values.orgs],
                    modules: modules,
                    permissions: permissions,
                    status: 'verification',
                };
                authService.staffCreate(body).then((res) => {
                    if (res.status) {
                        resetForm();
                        setIsOpen(false);
                        setModules([]);
                        setPermissions(nonePermission);
                        loadData();
                        toast.success('New staff added.', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                    } else {
                        toast.error(res.msg, { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                    }
                    setSubmit(false);
                });
            }

            if (isOpen && isEdit) {
                setSubmit(true);
                let body = {
                    uid: eid,
                    data: {
                        fname: values.fname,
                        mname: values.mname,
                        lname: values.lname,
                        email: values.email,
                        phone: values.phone,
                        addresses: [
                            {
                                title: "HOME",
                                default: true,
                                street_1: values.street_1,
                                street_2: values.street_2,
                                city: values.city.toUpperCase(),
                                state: values.state.toUpperCase(),
                                country: values.country.toUpperCase(),
                                postal_code: values.postal_code,
                            }
                        ],
                        media: {},
                        role: values.role,
                        orgs: [values.orgs],
                        modules: modules,
                        permissions: permissions,
                    }
                };
                authService.updateStaff(body).then((res) => {
                    if (res.status) {
                        resetForm();
                        setIsOpen(false);
                        setTempDoc({});
                        setEid('');
                        setIsEdit(false);
                        setModules([]);
                        setPermissions(nonePermission);
                        loadData();
                        toast.success('Staff updated.', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                    } else {
                        toast.error(res.msg, { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                    }
                    setSubmit(false);
                });
            }
        }
    });

    const changeTab = (val) => {
        setTab(val);
        setQuery('');
        setDate({
            from: Moment().startOf('year').toDate(),
            to: Moment().toDate(),
        });
        switch (val) {
            case "all":
                setFilter({});
                break;
            case "active":
                setFilter({ 'status': 'active' });
                break;
            case "banned":
                setFilter({ 'status': 'banned' });
                break;
            default:
                break;
        }
    }

    const handleOnChange = (e) => {
        var { value, name } = e.target;
        switch (name) {
            case 'pageSize':
                setPageSize(value);
                break;
            case 'search':
                setQuery(value);
                break;
        }
    }

    const switchModule = (module) => {
        let tempModule = [...modules];
        if (tempModule.includes(module)) {
            var index = tempModule.findIndex((e) => e === module);
            tempModule.splice(index, 1);
            setPermissions((prev) =>
                prev.filter((p) => !p.includes(module))
            );
            togglePermission(`${module}.NONE`);
        } else {
            tempModule.push(module);
        }
        setModules(tempModule);
    }

    const togglePermission = (permission) => {
        setPermissions((prev) =>
            prev.includes(permission)
                ? prev.filter((p) => p !== permission)
                : [...prev, permission]
        );
    };

    const addMultiplePermissions = (perms) => {
        setPermissions((prev) => [
            ...new Set([...prev, ...perms])
        ]);
    };

    const removeMultiplePermissions = (perms) => {
        setPermissions((prev) =>
            prev.filter((p) => !perms.includes(p))
        );
    };

    const onCheck = (permission) => {
        switch (permission) {
            case "SUBSCRIPTION.ALL":
                if (permissions.includes("SUBSCRIPTION.ALL")) {
                    addMultiplePermissions(["SUBSCRIPTION.NONE"]);
                    togglePermission(permission);
                } else {
                    removeMultiplePermissions(["SUBSCRIPTION.NONE"]);
                    togglePermission(permission);
                }
                break;
            case "TRANSACTION.ALL":
                if (permissions.includes("TRANSACTION.ALL")) {
                    addMultiplePermissions(["TRANSACTION.NONE"]);
                    togglePermission(permission);
                } else {
                    removeMultiplePermissions(["TRANSACTION.NONE"]);
                    togglePermission(permission);
                }
                break;
            case "STAFF.ALL":
                if (permissions.includes("STAFF.ALL")) {
                    addMultiplePermissions(["STAFF.NONE"]);
                    togglePermission(permission);
                } else {
                    removeMultiplePermissions(["STAFF.NONE"]);
                    togglePermission(permission);
                }
                break;
            case "RESTAURANTS.ALL":
                if (permissions.includes("RESTAURANTS.ALL")) {
                    addMultiplePermissions(["RESTAURANTS.NONE"]);
                    removeMultiplePermissions(resPermission);
                } else {
                    removeMultiplePermissions(["RESTAURANTS.NONE"]);
                    addMultiplePermissions(resPermission);
                }
                break;
            default:
                togglePermission(permission);
                break;
        }
    }

    const onDateChange = (event) => {
        setIsDate(true);
        setDate(event);
    }

    const addStaff = () => {
        setTempDoc({});
        setEid('');
        setIsEdit(false);
        setModules([]);
        setPermissions(nonePermission);
        setIsOpen(true);
    }

    const updateStatus = (id, status) => {
        let body = {
            uid: id,
            data: {
                status: status
            }
        }
        authService.updateStaffById(body).then((res) => {
            if (res['status']) {
                loadData();
            } else {
                toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        });
    }

    const changePassword = (id) => {
        authService.sendReset({ uid: id }).then((res) => {
            if (res.status) {
                toast.success('Password reset link sent to registered email address.', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            } else {
                toast.error(res.msg, { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
            setSubmit(false);
        });
    }

    const editRecord = (doc) => {
        setTempDoc(doc);
        setEid(doc._id);
        setIsEdit(true);
        setIsOpen(true);
    }

    const pageChange = (pageValue) => {
        (async function () {
            let body = {
                "filter": filter,
                "page": pageValue,
                "size": parseInt(pageSize),
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? Moment(date.to).add(1, 'day').toDate().toISOString() : ""
            };
            var res = await authService.getAllStaff(body, '');
            if (res['status']) {
                setPageCurrent(res.data.currentPage);
                setPageTotal(res.data.totalPage);
                setRecords(res.data.data);
            } else {
                toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        })();
    }

    const loadData = () => {
        (async function () {
            let body = {
                "filter": filter,
                "page": 1,
                "size": parseInt(pageSize),
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? Moment(date.to).add(1, 'day').toDate().toISOString() : ""
            };
            var res = await authService.getAllStaff(body, '');
            if (res['status']) {
                setPageCurrent(res.data.currentPage);
                setPageTotal(res.data.totalPage);
                setRecords(res.data.data);
            } else {
                toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }

            let bodyOrg = {
                "filter": {},
                "page": 0,
                "size": 0,
                "searchQuery": "",
                "startDate": "",
                "endDate": ""
            };
            var resOrg = await orgService.getAllOrgs(bodyOrg, '');
            setOrgs(resOrg.data.data);
        })();
    }

    const nextPage = () => {
        if (pageCurrent < pageTotal) {
            pageChange(pageCurrent + 1);
        }
    }

    const prevPage = () => {
        if (pageCurrent > 1) {
            pageChange(pageCurrent - 1);
        }
    }

    const populdata = () => {
        setValues({
            role: tempDoc.role,
            orgs: tempDoc.orgs ? tempDoc.orgs[0] : '',
            fname: tempDoc.user.fname,
            mname: tempDoc.user.mname,
            lname: tempDoc.user.lname,
            email: tempDoc.user.email,
            phone: tempDoc.user.phone,
            street_1: tempDoc.user.addresses[0] === undefined ? "" : tempDoc.user.addresses[0].street_1,
            street_2: tempDoc.user.addresses[0] === undefined ? "" : tempDoc.user.addresses[0].street_2,
            city: tempDoc.user.addresses[0] === undefined ? "" : tempDoc.user.addresses[0].city,
            state: tempDoc.user.addresses[0] === undefined ? "" : tempDoc.user.addresses[0].state,
            country: tempDoc.user.addresses[0] === undefined ? "" : tempDoc.user.addresses[0].country,
            postal_code: tempDoc.user.addresses[0] === undefined ? "" : tempDoc.user.addresses[0].postal_code
        });
        setModules(tempDoc.modules);
        setPermissions(tempDoc.permissions);
    }

    const downloadFile = ({ data, fileName, fileType }) => {
        const blob = new Blob([data], { type: fileType })
        const a = document.createElement('a')
        a.download = fileName
        a.href = window.URL.createObjectURL(blob)
        const clickEvt = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
        })
        a.dispatchEvent(clickEvt)
        a.remove()
    }

    const exportData = (type) => {
        (async function () {
            let body = {
                "filter": filter,
                "page": 0,
                "size": 0,
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? Moment(date.to).add(1, 'day').toDate().toISOString() : ""
            };
            var res = await authService.getAllStaff(body);
            var records = res.data.data;
            let items = [];
            records.forEach((item) => {
                items.push({
                    fname: item.user.fname,
                    mname: item.user.mname,
                    lname: item.user.lname,
                    email: item.user.email,
                    phone: item.user.phone,
                    dob: item.user.dob,
                    role: item.role,
                    status: item.status,
                    createdAt: item.createdAt,
                });
            });

            if (type === "xlsx") {
                const worksheet = XLSX.utils.json_to_sheet(items);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "staff");
                XLSX.writeFile(workbook, "Staff.xlsx");
            } else {
                let headers = ['fname, mname, lname, email, phone, dob, role, status, createdAt'];
                let recordCsv = items.reduce((acc, record) => {
                    const { fname, mname, lname, email, phone, dob, role, status, createdAt } = record
                    acc.push([fname, mname, lname, email, phone, dob, role, status, createdAt].join(','))
                    return acc
                }, []);
                downloadFile({
                    data: [...headers, ...recordCsv].join('\n'),
                    fileName: 'Staff.csv',
                    fileType: 'text/csv',
                });
            }
        })();
    }

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        loadData();
    }, [pageSize, query, date, filter]);

    useEffect(() => {
        if (isOpen && isEdit) {
            populdata();
        }
    }, [isOpen, isEdit, tempDoc]);

    return (
        <>
            {
                isOpen && <div className='overflow-y-auto overflow-x-hidden z-[100] w-full h-modal md:h-full fixed top-0 left-0 bg-black bg-opacity-70'>
                    <div className='bg-white w-[70%] min-h-screen absolute right-0 p-[20px]'>
                        <ScrollArea>
                            <div className='px-2'>
                                <div className='flex items-center justify-between text-2xl'>
                                    <h1 className='font-sans font-medium text-gray-900 uppercase'>{isEdit ? "UPDATE" : "ADD"} STAFF</h1>
                                    <div className='w-7 h-7 text-gray-700 flex items-center justify-center border border-black rounded-full hover:bg-black hover:text-white cursor-pointer' onClick={() => {
                                        setIsOpen(false);
                                        resetForm();
                                    }}>
                                        <span><i className="las la-times text-xl cursor-pointer"></i></span>
                                    </div>
                                </div>
                                <hr className='my-2' />
                                <form className='mt-4 grid grid-cols-12 gap-4' onSubmit={handleSubmit} onReset={handleReset} noValidate="" autoComplete='off'>
                                    <div className="col-span-4">
                                        <label className="block text-xs font-medium text-gray-700 font-mono">Staff Type<sup className="text-red-600">*</sup></label>
                                        <select id="role" className="border-gray-300 border rounded appearance-none w-full py-1 px-2 bg-white mt-2" onChange={handleChange} value={values.role}>
                                            <option value="" disabled="" selected="selected">Select</option>
                                            {
                                                userTypes.length !== 0 && userTypes.map((item) => {
                                                    return <option value={item.value} selected={values.role == item.value}>{item.label}</option>
                                                })
                                            }
                                        </select>
                                        {(errors.role && touched.role) && <p className='text-xs text-red-400 mt-1'>{errors.role}</p>}
                                    </div>

                                    {
                                        values.role === "PARTNER" && <div className="col-span-4">
                                            <label className="block text-xs font-medium text-gray-700 font-mono">Select Organization<sup className="text-red-600">*</sup></label>
                                            <select id="orgs" className="border-gray-300 border rounded appearance-none w-full py-1 px-2 bg-white mt-2" onChange={handleChange} value={values.orgs}>
                                                <option value="" disabled="" selected="selected">Select</option>
                                                {
                                                    orgs.length !== 0 && orgs.map((item) => {
                                                        return <option value={item.ordid} selected={values.orgs == item.ordid} className='text-sm'>
                                                            {item.name} - {item.ordid}
                                                        </option>
                                                    })
                                                }
                                            </select>
                                            {(errors.orgs && touched.orgs) && <p className='text-xs text-red-400 mt-1'>{errors.orgs}</p>}
                                        </div>
                                    }

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">First Name<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="fname" value={values.fname} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Jhon" onChange={handleChange} />
                                        {(errors.fname && touched.fname) && <p className='text-xs text-red-400 mt-1'>{errors.fname}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Maiden Name<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="mname" value={values.mname} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Sam" onChange={handleChange} />
                                        {(errors.mname && touched.mname) && <p className='text-xs text-red-400 mt-1'>{errors.mname}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Last Name<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="lname" value={values.lname} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Deo" onChange={handleChange} />
                                        {(errors.lname && touched.lname) && <p className='text-xs text-red-400 mt-1'>{errors.lname}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Email Address<sup className="text-red-600">*</sup></label>
                                        <input type="email" id="email" value={values.email} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. jhon.deo@gmail.com" onChange={handleChange} />
                                        {(errors.email && touched.email) && <p className='text-xs text-red-400 mt-1'>{errors.email}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Phone Number<sup className="text-red-600">*</sup></label>
                                        <input type="number" id="phone" value={values.phone} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxxxxxxxx" onChange={handleChange} />
                                        {(errors.phone && touched.phone) && <p className='text-xs text-red-400 mt-1'>{errors.phone}</p>}
                                    </div>

                                    <div className="col-span-12">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Street Address 1<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="street_1" value={values.street_1} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Apt, Building " onChange={handleChange} />
                                        {(errors.street_1 && touched.street_1) && <p className='text-xs text-red-400 mt-1'>{errors.street_1}</p>}
                                    </div>

                                    <div className="col-span-12">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Street Address 2<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="street_2" value={values.street_2} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Street address, Landmark" onChange={handleChange} />
                                        {(errors.street_2 && touched.street_2) && <p className='text-xs text-red-400 mt-1'>{errors.street_2}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">City<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="city" value={values.city} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Mumbai" onChange={handleChange} />
                                        {(errors.city && touched.city) && <p className='text-xs text-red-400 mt-1'>{errors.city}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">State<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="state" value={values.state} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Maharashtra" onChange={handleChange} />
                                        {(errors.state && touched.state) && <p className='text-xs text-red-400 mt-1'>{errors.state}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Country<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="country" value={values.country} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. India" onChange={handleChange} />
                                        {(errors.country && touched.country) && <p className='text-xs text-red-400 mt-1'>{errors.country}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Postal code<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="postal_code" value={values.postal_code} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxxxx" onChange={handleChange} />
                                        {(errors.postal_code && touched.postal_code) && <p className='text-xs text-red-400 mt-1'>{errors.postal_code}</p>}
                                    </div>

                                    {
                                        values.role != "PARTNER" && <div className="col-span-12">
                                            <label className="block text-xs font-medium text-gray-700 font-mono">Subscription Permissions<sup className="text-red-600">*</sup></label>
                                            <div className='mt-1 flex items-center'>
                                                <Switch checked={modules.includes('SUBSCRIPTION')} onCheckedChange={(e) => { switchModule('SUBSCRIPTION'); }} className="mr-6" />
                                                {
                                                    modules.includes('SUBSCRIPTION') && <div className='flex items-center space-x-2'>
                                                        <div className='flex items-center'>
                                                            <Checkbox checked={permissions.includes('SUBSCRIPTION.ALL')} onCheckedChange={(e) => { onCheck('SUBSCRIPTION.ALL'); }} />
                                                            <h2 className='text-sm font-medium ml-2'>SUBSCRIPTION_ALL</h2>
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    }

                                    {
                                        values.role != "PARTNER" && <div className="col-span-12">
                                            <label className="block text-xs font-medium text-gray-700 font-mono">Transation Permissions<sup className="text-red-600">*</sup></label>
                                            <div className='mt-1 flex items-center'>
                                                <Switch checked={modules.includes('TRANSACTION')} onCheckedChange={(e) => { switchModule('TRANSACTION'); }} className="mr-6" />
                                                {
                                                    modules.includes('TRANSACTION') && <div className='flex items-center space-x-2'>
                                                        <div className='flex items-center'>
                                                            <Checkbox checked={permissions.includes('TRANSACTION.ALL')} onCheckedChange={(e) => { onCheck('TRANSACTION.ALL'); }} />
                                                            <h2 className='text-sm font-medium ml-2'>TRANSACTION_ALL</h2>
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    }

                                    {
                                        values.role != "PARTNER" && <div className="col-span-12">
                                            <label className="block text-xs font-medium text-gray-700 font-mono">Staff Permissions<sup className="text-red-600">*</sup></label>
                                            <div className='mt-1 flex items-center'>
                                                <Switch checked={modules.includes('STAFF')} onCheckedChange={(e) => { switchModule('STAFF'); }} className="mr-6" />
                                                {
                                                    modules.includes('STAFF') && <div className='flex items-center space-x-2'>
                                                        <div className='flex items-center'>
                                                            <Checkbox checked={permissions.includes('STAFF.ALL')} onCheckedChange={(e) => { onCheck('STAFF.ALL'); }} />
                                                            <h2 className='text-sm font-medium ml-2'>STAFF_ALL</h2>
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    }

                                    {
                                        values.role != "PARTNER" && <div className="col-span-12">
                                            <label className="block text-xs font-medium text-gray-700 font-mono">Restaurants Permissions<sup className="text-red-600">*</sup></label>
                                            <div className='mt-1 flex items-center'>
                                                <Switch checked={modules.includes('RESTAURANTS')} onCheckedChange={(e) => { switchModule('RESTAURANTS'); }} className="mr-6" />
                                                {
                                                    modules.includes('RESTAURANTS') && <div className='flex items-center space-x-2'>
                                                        <div className='flex items-center'>
                                                            <Checkbox checked={permissions.includes('RESTAURANTS.ALL')} onCheckedChange={(e) => { onCheck('RESTAURANTS.ALL'); }} />
                                                            <h2 className='text-sm font-medium ml-2'>RESTAURANTS_ALL</h2>
                                                        </div>
                                                        <div className='flex items-center'>
                                                            <Checkbox checked={permissions.includes('RESTAURANTS.VIEWONLY')} onCheckedChange={(e) => { onCheck('RESTAURANTS.VIEWONLY'); }} />
                                                            <h2 className='text-sm font-medium ml-2'>RESTAURANTS_VIEWONLY</h2>
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    }

                                    <div className='col-span-12 my-6'>
                                        <div className='flex items-end'>
                                            <button type="submit" className="text-white bg-prime hover:bg-primeLight focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center" disabled={submit}>
                                                {submit && <Lottie animationData={animBtnLoading} className="w-8 h-8" loop={true} />}
                                                {!submit && "SAVE"}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </ScrollArea >
                    </div >
                </div >
            }
            <ScrollArea className='h-screen w-full'>
                <div className="w-full h-full py-3 px-3">
                    <div className="grid grid-cols-12 grid-gap-2">
                        <div className="col-span-12 mt-2">
                            <div class="intro-y flex items-center justify-between h-10 border-b pb-4">
                                <h2 class="text-2xl font-medium font-serif truncate ml-2">
                                    Staff Master
                                </h2>
                                <TopBar />
                            </div>
                            <div class="w-full mt-4 flex justify-between items-center">
                                <div className='h-10 bg-gray-100 rounded'>
                                    <ul className="intro-y flex text-sm font-sans">
                                        <li className={`px-3 h-6 m-2 rounded flex items-center justify-center ${tab === 'all' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { changeTab('all') }}>All</button></li>
                                        <li className={`px-3 h-6 m-2 rounded flex items-center justify-center ${tab === 'active' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { changeTab('active') }}>Active</button></li>
                                        <li className={`px-3 h-6 m-2 rounded flex items-center justify-center ${tab === 'banned' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { changeTab('banned') }}>Banned</button></li>
                                    </ul>
                                </div>
                                <button className="h-8 py-1 px-2 rounded-md bg-prime hover:bg-primeLight text-white mr-2 text-sm" onClick={() => { addStaff() }}><i class="las la-plus mr-1"></i> ADD STAFF</button>
                            </div>
                        </div>
                    </div>
                    <div className="col-span-12 mt-4">
                        <div className="w-full shadow-md rounded-md border">
                            <table className="min-w-full divide-y divide-gray-200 px-4">
                                <thead className="bg-gray-50">
                                    <tr className=''>
                                        <th scope="col" className="border-b border-gray-200" colSpan={9}>
                                            <div className='w-full grid grid-cols-12 gap-2'>
                                                <form className="items-center py-2 col-span-11 px-2 grid grid-cols-12 gap-2">
                                                    <div className='col-span-3 flex'>
                                                        <Popover>
                                                            <PopoverTrigger asChild>
                                                                <Button id="date" variant={"outline"}
                                                                    className={cn(
                                                                        "w-[300px] justify-start text-left font-normal",
                                                                        !date && "text-muted-foreground"
                                                                    )}
                                                                >
                                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                                    {date?.from ? (
                                                                        date.to ? (
                                                                            <>
                                                                                {Moment(date.from).format('MMM DD, yyyy')} -{" "}
                                                                                {Moment(date.to).format('MMM DD, yyyy')}
                                                                            </>
                                                                        ) : (
                                                                            Moment(date.from).format('MMM DD, yyyy')
                                                                        )
                                                                    ) : (
                                                                        <span>Pick a date</span>
                                                                    )}
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent className="w-auto p-0" align="start">
                                                                <Calendar
                                                                    initialFocus
                                                                    mode="range"
                                                                    defaultMonth={date?.from}
                                                                    selected={date}
                                                                    onSelect={onDateChange}
                                                                    numberOfMonths={2}
                                                                />
                                                            </PopoverContent>
                                                        </Popover>
                                                    </div>
                                                    <div className='w-full flex col-span-9 items-center'>
                                                        <div className='w-[80px]'>
                                                            <p>Search :</p>
                                                        </div>
                                                        <input type="text" value={query} onChange={handleOnChange} name='search' placeholder="Search by name and email." className="w-full h-10 px-4 font-sans text-sm outline-none rounded border" />
                                                    </div>
                                                </form>
                                                <div className='col-span-1 flex items-center'>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger>
                                                            <button className="w-full py-1 px-2 h-10 rounded-md bg-prime hover:bg-primeLight text-white mr-4"><i className="las la-file-download mr-2"></i> Export </button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent>
                                                            <div
                                                                className='bg-white shadow-md rounded-md absolute' style={{ top: '0px', left: '-64px' }}>
                                                                <div>
                                                                    <ul className='flex flex-col items-start p-2'>
                                                                        <DropdownMenuItem>
                                                                            <li className="w-40 cursor-pointer p-2 rounded-md" onClick={() => {
                                                                                exportData('xlsx');
                                                                            }}>
                                                                                <div className='flex items-center text-base'>
                                                                                    <p className="text-sm font-serif ml-2 font-normal">Export to .xlsx</p>
                                                                                </div>
                                                                            </li>
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem>
                                                                            <li className="w-40 cursor-pointer p-2 rounded-md" onClick={() => {
                                                                                exportData('csv');
                                                                            }}>
                                                                                <div className='flex items-center text-base'>
                                                                                    <p className="text-sm font-serif ml-2 font-normal">Export to .csv</p>
                                                                                </div>
                                                                            </li>
                                                                        </DropdownMenuItem>
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                            </div>
                                        </th>
                                    </tr>
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            USER ID
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Name
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Email ID
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Phone Number
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            User Type
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            ORG Access
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Status
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Created At
                                        </th>
                                        <th scope="col" className="px-2 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {
                                        records.length !== 0 && records.map((record) => {
                                            return <tr className="hover:bg-gray-100" key={record._id}>
                                                <td className="px-6 py-4 whitespace-nowrap cursor-pointer">
                                                    <div className="text-sm font-bold text-gray-900">
                                                        <div className='flex items-center text-prime'>
                                                            #{record.userid}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-bold text-gray-900 uppercase">
                                                        {record.user.fname} {record.user.lname}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-bold text-gray-900">
                                                        {record.user.email}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-bold text-gray-900">
                                                        +91 {record.user.phone}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-prime">
                                                        {record.role}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-prime">
                                                        {record.orgs ? record.orgs : "ALL"}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase ${record.status == 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                        {record.status.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-bold text-gray-900">
                                                        {Moment(record.createdAt).format('DD MMM, yyyy hh:mm:ss A')}
                                                    </div>
                                                </td>
                                                <td>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger>
                                                            <motion.i whileHover={{ scale: 1.1 }} class="las la-ellipsis-v text-lg"></motion.i>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent>
                                                            <div>
                                                                <ul className='flex flex-col items-start'>
                                                                    <DropdownMenuItem>
                                                                        <li className='w-32 cursor-pointer rounded-md' onClick={() => {
                                                                            editRecord(record);
                                                                        }}>
                                                                            <div className='flex items-center text-base'>
                                                                                <i className="las la-edit text-lg"></i>
                                                                                <p className='text-sm font-serif ml-2'>Edit</p>
                                                                            </div>
                                                                        </li>
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem>
                                                                        <li className='w-32 cursor-pointer rounded-md' onClick={() => {
                                                                            changePassword(record.userid);
                                                                        }}>
                                                                            <div className='flex items-center text-base'>
                                                                                <i className="las la-lock text-lg"></i>
                                                                                <p className='text-sm font-serif ml-2'>Reset Password</p>
                                                                            </div>
                                                                        </li>
                                                                    </DropdownMenuItem>
                                                                    {
                                                                        record.status !== 'banned' && <DropdownMenuItem>
                                                                            <li className='w-32 cursor-pointer rounded-md' onClick={() => {
                                                                                updateStatus(record._id, 'banned');
                                                                            }}>
                                                                                <div className='flex items-center text-base'>
                                                                                    <i className="las la-ban text-lg"></i>
                                                                                    <p className='text-sm font-serif ml-2'>Block</p>
                                                                                </div>
                                                                            </li>
                                                                        </DropdownMenuItem>
                                                                    }
                                                                    {
                                                                        record.status === 'banned' && <DropdownMenuItem>
                                                                            <li className='w-32 cursor-pointer rounded-md' onClick={() => {
                                                                                updateStatus(record._id, 'active');
                                                                            }}>
                                                                                <div className='flex items-center text-base'>
                                                                                    <i className="las la-chevron-circle-up text-lg"></i>
                                                                                    <p className='text-sm font-serif ml-2'>Active</p>
                                                                                </div>
                                                                            </li>
                                                                        </DropdownMenuItem>
                                                                    }
                                                                    {
                                                                        record.status == 'banned' && <DropdownMenuItem>
                                                                            <li className='w-32 cursor-pointer rounded-md' onClick={() => {
                                                                                updateStatus(record._id, 'deleted');
                                                                            }}>
                                                                                <div className='flex items-center text-base'>
                                                                                    <i className="las la-trash text-lg"></i>
                                                                                    <p className='text-sm font-serif ml-2'>Delete</p>
                                                                                </div>
                                                                            </li>
                                                                        </DropdownMenuItem>
                                                                    }
                                                                </ul>
                                                            </div>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </td>
                                            </tr>
                                        })
                                    }
                                    {
                                        records.length === 0 && <tr className="cursor-pointer hover:bg-gray-100">
                                            <td className="px-6 py-4 whitespace-nowrap" colSpan="9">
                                                <div className="text-sm text-red-600 text-center">
                                                    No data found.
                                                </div>
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                            <div className='flex py-2 px-4 items-center bg-blue-50'>
                                <p className='text-sm font-serif'>Show rows per page</p>
                                <select className='text-sm ml-2 bg-gray-100' name="pageSize" onChange={handleOnChange} value={pageSize}>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={40}>40</option>
                                    <option value={50}>50</option>
                                </select>
                                <div className='flex-grow'></div>
                                <p className='text-sm font-serif mr-2'>Page {pageCurrent} of {pageTotal}</p>
                                <div onClick={() => { prevPage() }}><i className={`las la-angle-left w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 ${pageCurrent <= 1 ? 'text-gray-400 cursor-not-allowed' : 'text-black cursor-pointer'}`}></i></div>
                                <div onClick={() => { nextPage() }}><i className={`las la-angle-right w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 ml-2 text-black ${pageCurrent === pageTotal ? 'text-gray-400 cursor-not-allowed' : 'text-black cursor-pointer'}`}></i></div>
                            </div>
                        </div>
                    </div>
                </div >
            </ScrollArea>
        </>
    )
}
